import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { 
  Users, 
  HelpCircle, 
  Mail, 
  Activity,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: null,
    totalFaqs: 0,
    totalInquiries: 0,
    pendingInquiries: 0
  });
  const [loading, setLoading] = useState(true);
  const [healthStatus, setHealthStatus] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch health status
      const healthRes = await axios.get(`${API_URL}/api/health`);
      setHealthStatus(healthRes.data);

      // Fetch FAQs count
      const faqsRes = await axios.get(`${API_URL}/api/faqs`);
      
      // Fetch inquiries (if user has permission)
      let inquiriesData = [];
      try {
        const inquiriesRes = await axios.get(`${API_URL}/api/admin/inquiries`);
        inquiriesData = inquiriesRes.data;
      } catch (e) {
        // User might not have permission
      }

      // Fetch users count (only for admin/owner)
      let usersCount = null;
      try {
        const usersRes = await axios.get(`${API_URL}/api/admin/users`);
        usersCount = usersRes.data.length;
      } catch (e) {
        // User doesn't have permission - keep as null
      }

      setStats({
        totalUsers: usersCount,
        totalFaqs: faqsRes.data.length,
        totalInquiries: inquiriesData.length,
        pendingInquiries: inquiriesData.filter(i => i.status === 'pending').length
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, subtext }) => (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:border-slate-600/50 transition-all">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm font-medium mb-1">{label}</p>
          <p className="text-3xl font-bold text-white">{loading ? '-' : value}</p>
          {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-2" data-testid="dashboard-title">
          {getGreeting()}, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-slate-400">
          Welcome to the Coinexus admin dashboard. Here's what's happening today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${stats.totalUsers !== null ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-8`}>
        {stats.totalUsers !== null && (
          <StatCard 
            icon={Users}
            label="Total Users"
            value={stats.totalUsers}
            color="bg-blue-500/20 text-blue-400"
            subtext="Admin accounts"
          />
        )}
        <StatCard 
          icon={HelpCircle}
          label="Active FAQs"
          value={stats.totalFaqs}
          color="bg-emerald-500/20 text-emerald-400"
          subtext="Published questions"
        />
        <StatCard 
          icon={Mail}
          label="Total Inquiries"
          value={stats.totalInquiries}
          color="bg-purple-500/20 text-purple-400"
          subtext="Contact submissions"
        />
        <StatCard 
          icon={Clock}
          label="Pending"
          value={stats.pendingInquiries}
          color="bg-amber-500/20 text-amber-400"
          subtext="Awaiting response"
        />
      </div>

      {/* System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Health Status Card */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-400" />
            System Status
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-300">API Server</span>
              <span className={`flex items-center gap-2 text-sm font-medium ${
                healthStatus?.status === 'healthy' ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {healthStatus?.status === 'healthy' ? (
                  <><CheckCircle className="w-4 h-4" /> Operational</>
                ) : (
                  <><AlertCircle className="w-4 h-4" /> {healthStatus?.status || 'Checking...'}</>
                )}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
              <span className="text-slate-300">Database</span>
              <span className={`flex items-center gap-2 text-sm font-medium ${
                healthStatus?.database === 'connected' ? 'text-emerald-400' : 'text-red-400'
              }`}>
                {healthStatus?.database === 'connected' ? (
                  <><CheckCircle className="w-4 h-4" /> Connected</>
                ) : (
                  <><AlertCircle className="w-4 h-4" /> {healthStatus?.database || 'Checking...'}</>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Info Card */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-400" />
            Quick Info
          </h2>
          <div className="space-y-4">
            <div className="p-3 bg-slate-900/50 rounded-lg">
              <p className="text-slate-400 text-sm mb-1">Your Role</p>
              <p className="text-white font-medium capitalize">{user?.role}</p>
            </div>
            <div className="p-3 bg-slate-900/50 rounded-lg">
              <p className="text-slate-400 text-sm mb-1">Last Login</p>
              <p className="text-white font-medium">
                {user?.last_login 
                  ? new Date(user.last_login).toLocaleString()
                  : 'First login'
                }
              </p>
            </div>
            <div className="p-3 bg-slate-900/50 rounded-lg">
              <p className="text-slate-400 text-sm mb-1">Account Email</p>
              <p className="text-white font-medium">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
